import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';

const AdminManageBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [courts, setCourts] = useState([]);

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    courtId: 'all',
    status: 'all',
    customerName: '',
    referenceNumber: '',
    discountFilter: 'all', // 'all', 'discounted', 'no_discount'
    bulkOnly: false // filter for bulk bookings (bill numbers like 125-1, 125-2)
  });

  const [showFilters, setShowFilters] = useState(true); // mobile collapse
  const { token } = useAuth();

  const fetchCourts = async () => {
    try {
      const response = await fetch(`${API_URL}/courts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setCourts(data.courts || data.data || []);
      }
    } catch (err) {
      console.error('Error fetching courts:', err);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        const bookingsData = data.bookings || data.data || [];
        setBookings(bookingsData);
        setFilteredBookings(bookingsData);
      } else if (Array.isArray(data)) {
        setBookings(data);
        setFilteredBookings(data);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if a booking is a bulk booking (bill number pattern like 125-1, 125-2)
  const isBulkBooking = (booking) => {
    return booking.bill_number && /^\d+-\d+$/.test(booking.bill_number);
  };

  const applyFilters = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      if (filters.startDate) queryParams.append('start_date', filters.startDate);
      if (filters.endDate) queryParams.append('end_date', filters.endDate);
      if (filters.courtId && filters.courtId !== 'all') queryParams.append('court_id', filters.courtId);
      if (filters.status && filters.status !== 'all') queryParams.append('status', filters.status);
      if (filters.customerName.trim()) queryParams.append('customer_name', filters.customerName.trim());
      if (filters.referenceNumber.trim()) queryParams.append('booking_reference', filters.referenceNumber.trim());

      const url = queryParams.toString()
        ? `${API_URL}/bookings/filtered?${queryParams.toString()}`
        : `${API_URL}/bookings`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        let results = data.bookings || data.data || [];

        // Apply discount filter client-side
        if (filters.discountFilter === 'discounted') {
          results = results.filter(b => b.discounts > 0 || (b.notes && b.notes.includes('[Discount:')));
        } else if (filters.discountFilter === 'no_discount') {
          results = results.filter(b => !b.discounts && !(b.notes && b.notes.includes('[Discount:')));
        }

        // Apply bulk booking filter client-side
        if (filters.bulkOnly) {
          results = results.filter(b => isBulkBooking(b));
          // Sort by bill number group (125-1, 125-2 together)
          results.sort((a, b) => {
            const aGroup = parseInt(a.bill_number?.split('-')[0]) || 0;
            const bGroup = parseInt(b.bill_number?.split('-')[0]) || 0;
            if (aGroup !== bGroup) return bGroup - aGroup; // Newest group first
            const aIdx = parseInt(a.bill_number?.split('-')[1]) || 0;
            const bIdx = parseInt(b.bill_number?.split('-')[1]) || 0;
            return aIdx - bIdx; // Within group, sort by index
          });
        }

        setFilteredBookings(results);
        setError('');
      }
    } catch (err) {
      console.error('Error applying filters:', err);
      setError('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      courtId: 'all',
      status: 'all',
      customerName: '',
      referenceNumber: '',
      discountFilter: 'all',
      bulkOnly: false
    });
    setFilteredBookings(bookings);
    setError('');
  };

  // Helper to check if booking has discount
  const hasDiscount = (booking) => {
    return booking.discounts > 0 || (booking.notes && booking.notes.includes('[Discount:'));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString.slice(0, 5);
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-700 border-green-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    };
    return styles[status] || styles.pending;
  };

  const getSourceBadge = (source) => {
    const styles = {
      frontdesk: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '🏢' },
      online: { bg: 'bg-purple-100', text: 'text-purple-700', icon: '🌐' },
      partner_api: { bg: 'bg-orange-100', text: 'text-orange-700', icon: '🤝' },
      mobile_app: { bg: 'bg-green-100', text: 'text-green-700', icon: '📱' }
    };
    const style = styles[source] || styles.online;
    return { ...style, label: source?.replace('_', ' ').toUpperCase() };
  };

  useEffect(() => {
    fetchCourts();
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading && filteredBookings.length === 0) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading bookings...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">

      {success && (
        <div className="admin-alert success">{success}</div>
      )}

      {error && (
        <div className="admin-alert error">{error}</div>
      )}

      {/* Filter Panel */}
      <div className="admin-card">
        <div className="admin-card-body">
          <div className="admin-form-row">
            <div className="admin-field">
              <label className="admin-label">Start Date</label>
              <input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} className="admin-input" />
            </div>
            <div className="admin-field">
              <label className="admin-label">End Date</label>
              <input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} className="admin-input" />
            </div>
            <div className="admin-field">
              <label className="admin-label">Court</label>
              <select value={filters.courtId} onChange={(e) => setFilters({ ...filters, courtId: e.target.value })} className="admin-select">
                <option value="all">All Courts</option>
                {courts.map(court => (
                  <option key={court.id} value={court.id}>{court.name || court.court_name}</option>
                ))}
              </select>
            </div>
            <div className="admin-field">
              <label className="admin-label">Status</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="admin-select">
                <option value="all">All Status</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="admin-field">
              <label className="admin-label">Customer</label>
              <input type="text" value={filters.customerName} onChange={(e) => setFilters({ ...filters, customerName: e.target.value })} placeholder="Search name" className="admin-input" />
            </div>
            <div className="admin-field">
              <label className="admin-label">Reference</label>
              <input type="text" value={filters.referenceNumber} onChange={(e) => setFilters({ ...filters, referenceNumber: e.target.value })} placeholder="Search ref" className="admin-input" />
            </div>
            <div className="admin-field">
              <label className="admin-label">Discount</label>
              <select value={filters.discountFilter} onChange={(e) => setFilters({ ...filters, discountFilter: e.target.value })} className="admin-select">
                <option value="all">All Bookings</option>
                <option value="discounted">Discounted Only</option>
                <option value="no_discount">No Discount</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={applyFilters} disabled={loading} className="admin-btn admin-btn-primary">Apply</button>
            <button onClick={clearFilters} className="admin-btn admin-btn-secondary">Clear</button>
            <button onClick={fetchBookings} className="admin-btn admin-btn-secondary">Refresh</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '16px', padding: '8px 12px', background: filters.bulkOnly ? '#dbeafe' : '#f3f4f6', border: `1px solid ${filters.bulkOnly ? '#3b82f6' : '#e5e7eb'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: filters.bulkOnly ? '600' : '400' }}>
              <input type="checkbox" checked={filters.bulkOnly} onChange={(e) => setFilters({ ...filters, bulkOnly: e.target.checked })} style={{ accentColor: '#3b82f6' }} />
              Bulk Bookings Only
            </label>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="admin-card">
        <div className="admin-card-header">Bookings ({filteredBookings.length})</div>

        {filteredBookings.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">No bookings</div>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  {filters.bulkOnly && <th>Bill #</th>}
                  <th>Court</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking, index) => {
                  // Visual grouping for bulk bookings
                  const currentGroup = booking.bill_number?.split('-')[0];
                  const prevGroup = index > 0 ? filteredBookings[index - 1]?.bill_number?.split('-')[0] : null;
                  const isNewGroup = filters.bulkOnly && currentGroup !== prevGroup;

                  return (
                  <tr key={booking.id} style={{
                    ...(hasDiscount(booking) ? { backgroundColor: '#fef3c7' } : {}),
                    ...(isNewGroup && index > 0 ? { borderTop: '3px solid #3b82f6' } : {})
                  }}>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{booking.booking_reference || 'N/A'}</td>
                    {filters.bulkOnly && (
                      <td style={{ fontWeight: '600', color: '#3b82f6', fontFamily: 'monospace' }}>
                        {booking.bill_number || '-'}
                      </td>
                    )}
                    <td>
                      <div style={{ fontWeight: 600 }}>{booking.court_name}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{booking.sport_name}</div>
                    </td>
                    <td>{formatDate(booking.booking_date)}</td>
                    <td>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{booking.customer_name}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{booking.customer_phone}</div>
                    </td>
                    <td>
                      {booking.customer_name === 'BLOCKED' ? (
                        <div style={{ fontWeight: 500, color: '#6b7280', fontSize: '12px' }}>Blocked</div>
                      ) : (
                        <>
                          <div style={{ fontWeight: 600, color: '#059669' }}>₹{booking.total_amount || booking.paid_amount || booking.amount_due || 0}</div>
                          {booking.discount_percentage > 0 && (
                            <div style={{ fontSize: '10px', color: '#f59e0b' }}>-{booking.discount_percentage}% off</div>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`admin-badge ${
                        booking.customer_name === 'BLOCKED' ? 'admin-badge-secondary' :
                        booking.status === 'confirmed' ? 'admin-badge-success' :
                        booking.status === 'cancelled' ? 'admin-badge-danger' :
                        'admin-badge-warning'
                      }`}>
                        {booking.customer_name === 'BLOCKED' ? 'blocked' : booking.status}
                      </span>
                      {booking.status === 'cancelled' && booking.cancelled_by_name && (
                        <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px' }}>
                          by {booking.cancelled_by_name}
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManageBookings;
